import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
  Chip,
  LinearProgress,
  Paper,
  alpha
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import SettingsIcon from '@mui/icons-material/Settings';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  height: "100%",
  padding: theme.spacing(2)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: theme.palette.secondary.main,
    opacity: 0,
    transition: 'opacity 0.2s ease'
  },
  '&:hover': {
    boxShadow: '0 6px 16px rgba(15, 40, 76, 0.1)',
    transform: 'translateY(-2px)',
    '&::before': {
      opacity: 1,
    }
  }
}));

interface AutopilotCampaign {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'paused' | 'completed';
  schedule: string;
  targetAudience: string;
  nextSend: string;
  totalSent: number;
  openRate: number;
  clickRate: number;
  category: 'always-on' | 'added' | 'available';
  description: string;
}

interface LayoutSection {
  id: string;
  name: string;
  items: string[];
  description: string;
}

const AutopilotTab: React.FC = () => {
  const selectedDealer = useSelector(selectDealerSelected);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  // Autopilot settings
  const [autopilotEnabled, setAutopilotEnabled] = useState(true);
  const [smartScheduling, setSmartScheduling] = useState(true);
  const [autoOptimization, setAutoOptimization] = useState(false);
  
  // Layout sections as per design document
  const [layoutSections] = useState<LayoutSection[]>([
    {
      id: '1',
      name: 'Always-On',
      description: 'Default: Unsold nurturing after 30 days',
      items: [
        'Default: Unsold nurturing after 30 days',
        'Edit option to update days'
      ]
    },
    {
      id: '2', 
      name: 'Added',
      description: 'Example: Sold nurturing (buyers 2016-2022)',
      items: [
        'Example: Sold nurturing (buyers 2016-2022)'
      ]
    },
    {
      id: '3',
      name: 'Available (Upsell/Upgrade)',
      description: 'Premium features and campaign types',
      items: [
        'Inbound leads',
        'Trade-in campaigns', 
        'Service nurturing',
        'In-equity campaigns',
        'Service appointment reminders',
        'Service appointment follow-up'
      ]
    }
  ]);

  const handleToggleAutopilot = useCallback((enabled: boolean) => {
    setAutopilotEnabled(enabled);
    setSuccessMessage(`Autopilot ${enabled ? 'enabled' : 'disabled'} successfully!`);
  }, []);

  const handleToggleSmartScheduling = useCallback((enabled: boolean) => {
    setSmartScheduling(enabled);
    setSuccessMessage(`Smart scheduling ${enabled ? 'enabled' : 'disabled'}!`);
  }, []);

  const handleToggleAutoOptimization = useCallback((enabled: boolean) => {
    setAutoOptimization(enabled);
    setSuccessMessage(`Auto-optimization ${enabled ? 'enabled' : 'disabled'}!`);
  }, []);

  const handleCampaignAction = useCallback((campaignId: string, action: 'pause' | 'resume' | 'edit') => {
    try {
      setLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        setSuccessMessage(`Campaign ${action} action completed successfully!`);
        setLoading(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error performing campaign action:', error);
      setError('Failed to perform action. Please try again.');
      setLoading(false);
    }
  }, []);

  const handleCreateNewCampaign = useCallback(() => {
    setSuccessMessage('New campaign creation will be available in the next update.');
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'default';
      default: return 'default';
    }
  };

  return (
    <MainContainer>
      <Paper 
        elevation={0}
        sx={{ 
          p: 2.5,
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          gap: 2,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AutoModeIcon 
            sx={{ 
              color: theme.palette.secondary.main,
              fontSize: 28 
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.secondary.main
              }}
            >
              Configure Page
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              Automate workflows with an "always-on" execution view, upsell options, and configurable nurturing
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleCreateNewCampaign}
            disabled={loading}
            sx={{ 
              minWidth: 160,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Create Campaign
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={() => setSuccessMessage('Campaign management will be available in the next update.')}
            sx={{ 
              minWidth: 160,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Manage Campaigns
          </Button>
        </Box>
      </Paper>

      {/* Key Capabilities */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          mb: 3
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.secondary.main,
            mb: 2
          }}
        >
          Key Capabilities
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Always-on campaign execution with automated triggers
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Smart scheduling based on customer engagement patterns
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Configurable nurturing workflows for different customer segments
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Performance optimization and upsell opportunities
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Autopilot Settings */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <SettingsIcon 
              sx={{ 
                color: theme.palette.secondary.main,
                fontSize: 24 
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.secondary.main
              }}
            >
              Autopilot Settings
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  p: 2.5, 
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  height: '100%'
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={autopilotEnabled}
                      onChange={(e) => handleToggleAutopilot(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                      Enable Autopilot
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Automatically send campaigns based on predefined triggers and schedules.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  p: 2.5, 
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  height: '100%',
                  opacity: !autopilotEnabled ? 0.6 : 1
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={smartScheduling}
                      onChange={(e) => handleToggleSmartScheduling(e.target.checked)}
                      color="secondary"
                      disabled={!autopilotEnabled}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                      Smart Scheduling
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  AI optimizes send times based on customer engagement patterns.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  p: 2.5, 
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  height: '100%',
                  opacity: !autopilotEnabled ? 0.6 : 1
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoOptimization}
                      onChange={(e) => handleToggleAutoOptimization(e.target.checked)}
                      color="secondary"
                      disabled={!autopilotEnabled}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                      Auto-Optimization
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Automatically optimize content and targeting based on performance.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 600,
          color: theme.palette.secondary.main,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        Autopilot Campaign Templates
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: 2.5,
          padding: 1,
        }}
      >
        {layoutSections.map((section) => (
          <StyledCard key={section.id}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box 
                sx={{ 
                  p: 2.5,
                  backgroundColor: '#0f284c',
                  color: 'white',
                  borderBottom: '1px solid',
                  borderColor: alpha('#ffffff', 0.1)
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <AutoModeIcon fontSize="small" />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}>
                    {section.name}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.8), fontSize: '0.85rem' }}>
                  {section.description}
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  p: 2.5, 
                  flexGrow: 1,
                  backgroundColor: alpha('#f5f8fa', 0.5),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}
              >
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.secondary.main,
                    mb: 1
                  }}
                >
                  Available Features:
                </Typography>
                {section.items.map((item, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 1, 
                      mb: 0.5,
                      p: 1.5,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', minWidth: '8px' }}>•</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
                
                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    size="small"
                    fullWidth
                    sx={{ 
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    {section.name === 'Available (Upsell/Upgrade)' ? 'Upgrade Now' : 'Configure'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </StyledCard>
        ))}
      </Box>

      {/* Autopilot Benefits */}
      <Paper 
        elevation={0}
        sx={{ 
          mt: 3, 
          p: 3, 
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <TipsAndUpdatesIcon 
            sx={{ 
              color: theme.palette.secondary.main,
              fontSize: 24 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.secondary.main
            }}
          >
            Autopilot Benefits
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.secondary.main }}>
                ⏰ Time Savings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Set up once and let campaigns run automatically based on triggers and schedules.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.secondary.main }}>
                🎯 Consistent Engagement
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Maintain regular communication with customers without manual intervention.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.secondary.main }}>
                📈 Performance Optimization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                AI continuously optimizes send times and content for better results.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.secondary.main }}>
                📊 Real-time Monitoring
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                Track campaign performance and make adjustments as needed.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default AutopilotTab;
